/**
 * How far a category's money lasts against the bills scheduled from it.
 *
 * The Fill screen says, beside each line, when the money it is about to give runs out.
 * A figure without a date answers the wrong question: "$593.66 to The Build" says
 * nothing about whether the payment on the 29th will clear, and that is what anyone
 * deciding to press the button wants to know.
 *
 * Bills are paid in date order from what the category holds. The first one it cannot
 * pay in full is where the coverage ends — a later, smaller bill it could afford does
 * not count, because the earlier one will have taken the money by then.
 */

import { type Milliunits, mu } from './money.ts'

export interface Bill {
  /** `YYYY-MM-DD`. */
  date: string
  /** What leaves, as a positive magnitude. */
  amount: Milliunits
}

export interface Coverage {
  /** The last bill paid in full, or null when not even the first one is. */
  coveredThrough: Bill | null
  /** The first bill that cannot be paid in full, and by how much. Null when all are. */
  firstShort: (Bill & { short: Milliunits }) | null
}

/** `holding` is what the category will have: what it holds now plus what it is given. */
export function coverage(holding: Milliunits, bills: readonly Bill[]): Coverage {
  const ordered = [...bills].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
  let left: number = holding
  let coveredThrough: Bill | null = null
  for (const bill of ordered) {
    if (left < bill.amount) {
      return { coveredThrough, firstShort: { ...bill, short: mu(bill.amount - Math.max(0, left)) } }
    }
    left -= bill.amount
    coveredThrough = bill
  }
  return { coveredThrough, firstShort: null }
}
